import classNames from 'classnames'
import { addDays, endOfDay, isAfter, isBefore, startOfDay, sub, subDays } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MoreHoriz } from '@mui/icons-material'
import { Box, FormControl, Grid, IconButton, Menu, MenuItem } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { isValidDate } from '../../helpers/date'
import {
  DateRangeOptionType,
  dateRangeOption30Days,
  dateRangeOption7Days,
  dateRangeOption90Days,
  dateRangeOptionAllAvailable,
  dateRangeOptionLastYear,
} from '../../types/DateRangeOptions'
import './DateRangePicker.scss'
import { clampDateRangeToBounds } from './clampDateRangeToBounds'

export type DateRangeValue = {
  fromDate?: Date | null
  toDate?: Date | null
}

/** `'user'` is typing/presets; `'bounds'` is an automatic min/max clamp (GR-2985). */
export type DateRangeChangeReason = 'user' | 'bounds'

interface DateRangePickerProps {
  onChangeValue?: (value?: DateRangeValue, reason?: DateRangeChangeReason) => void
  value?: DateRangeValue
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  dateRangeOptions?: DateRangeOptionType[]
  allowSameFromAndToDate?: boolean
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChangeValue,
  minDate,
  maxDate: maxDateProp,
  disabled,
  dateRangeOptions = [dateRangeOption7Days, dateRangeOption30Days, dateRangeOption90Days, dateRangeOptionLastYear, dateRangeOptionAllAvailable],
  allowSameFromAndToDate = true,
}) => {
  const maxDate = useMemo(() => maxDateProp || new Date(), [maxDateProp])
  const { t } = useTranslation()
  const [fromIsOpen, setFromIsOpen] = useState(false)
  const [toIsOpen, setToIsOpen] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [fromResetKey, setFromResetKey] = useState(0)
  const [toResetKey, setToResetKey] = useState(0)
  const dateRangeContainer = useRef(null)
  const rangeOptionOpener = useRef(null)
  const prevMinDateRef = useRef(minDate)
  const prevMaxDateRef = useRef(maxDate)

  const handleValueChange = useCallback(
    (value?: DateRangeValue, reason: DateRangeChangeReason = 'user') => {
      onChangeValue && onChangeValue(value, reason)
    },
    [onChangeValue]
  )

  // Clamps the selected date range when minDate/maxDate boundaries change.
  // Without this, switching context (e.g. game, tab) could leave fromDate/toDate
  // outside the new valid range, causing the picker to snap to today's date.
  // When the whole range falls outside a boundary, the window is shifted while
  // preserving its length instead of collapsing to a one-day range (GR-2985).
  useEffect(() => {
    // Detect whether the boundaries actually changed since last render
    const minDateChanged = prevMinDateRef.current?.getTime() !== minDate?.getTime()
    const maxDateChanged = prevMaxDateRef.current?.getTime() !== maxDate?.getTime()
    prevMinDateRef.current = minDate
    prevMaxDateRef.current = maxDate

    // Skip if boundaries haven't actually changed
    if (!minDateChanged && !maxDateChanged) return
    // Skip if no date range is currently selected
    if (!value) return

    const { fromDate, toDate, corrected } = clampDateRangeToBounds({ fromDate: value.fromDate, toDate: value.toDate, minDate, maxDate })

    // Push corrected values and force pickers to re-render
    if (corrected) {
      handleValueChange({ fromDate, toDate }, 'bounds')
      setFromResetKey((k) => k + 1)
      setToResetKey((k) => k + 1)
    }
  }, [value, minDate, maxDate, handleValueChange])

  const handleSelectRange = useCallback(
    (value?: number) => {
      const newToDate = value ? endOfDay(new Date()) : endOfDay(maxDate)
      let newFromDate
      if (!value) {
        newFromDate = minDate || endOfDay(1605615507000)
      } else {
        const fromDateCandidate = startOfDay(sub(newToDate, { days: value }))
        const isFromDateAfterMinDate = minDate && isAfter(fromDateCandidate, minDate) ? true : !minDate
        newFromDate = isFromDateAfterMinDate ? fromDateCandidate : minDate ? minDate : null
      }

      handleValueChange({ fromDate: newFromDate, toDate: newToDate })
      setShowOptions(false)
    },
    [handleValueChange, maxDate, minDate]
  )

  const handleShowOptionsClick = useCallback(() => {
    if (!disabled) {
      setShowOptions(!showOptions)
    }
  }, [showOptions, disabled])

  const dateRangeOptionsHtml = useMemo(() => {
    return dateRangeOptions.map((dateRangeOption) => {
      return (
        <MenuItem key={dateRangeOption.id} onClick={() => handleSelectRange(dateRangeOption.days)}>
          {t(dateRangeOption.text, dateRangeOption.translationObject)}
        </MenuItem>
      )
    })
  }, [t, handleSelectRange, dateRangeOptions])

  return (
    <FormControl className={classNames('DateRangePicker', { disabled })} ref={dateRangeContainer} disabled={disabled}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container direction="row" alignItems="center" justifyContent="center">
          <Grid item>
            <DatePicker
              key={fromResetKey}
              maxDate={allowSameFromAndToDate ? maxDate : value?.toDate ? subDays(value?.toDate, 1) : maxDate}
              minDate={minDate}
              onChange={(newValue: Date | null) => {
                if (!isValidDate(newValue)) return
                let dateValue = newValue as Date
                let clamped = false
                if (minDate && isBefore(dateValue, minDate)) {
                  dateValue = minDate
                  clamped = true
                }
                if (isAfter(dateValue, maxDate)) {
                  dateValue = maxDate
                  clamped = true
                }
                const newFromDateValue = startOfDay(dateValue)
                if (clamped) setFromResetKey((k) => k + 1)
                const newToDateValue = value?.toDate && isBefore(value?.toDate, newFromDateValue) ? endOfDay(newFromDateValue) : value?.toDate
                handleValueChange({ fromDate: newFromDateValue, toDate: newToDateValue })
              }}
              onClose={() => setFromIsOpen(false)}
              onOpen={() => setFromIsOpen(true)}
              slotProps={{
                inputAdornment: {
                  position: 'start',
                },
                textField: {
                  className: fromIsOpen ? 'active' : '',
                  hiddenLabel: true,
                  variant: 'filled',
                  size: 'small',
                  InputProps: {
                    disableUnderline: true,
                    sx: { backgroundColor: 'transparent', '&:hover, &:focus, &:active': { backgroundColor: 'transparent' } },
                  },
                },
              }}
              onAccept={(date: Date | null) => {
                setToIsOpen(true)
              }}
              value={value?.fromDate}
              disabled={disabled}
            />
          </Grid>
          <Grid item>
            <Box mx={1}> - </Box>
          </Grid>
          <Grid item>
            <DatePicker
              key={toResetKey}
              maxDate={maxDate}
              minDate={allowSameFromAndToDate ? value?.fromDate || minDate : value?.fromDate ? addDays(value?.fromDate, 1) : minDate}
              onChange={(newValue: Date | null) => {
                if (!isValidDate(newValue)) return
                let dateValue = newValue as Date
                let clamped = false
                const effectiveMinDate = value?.fromDate || minDate
                if (effectiveMinDate && isBefore(dateValue, effectiveMinDate)) {
                  dateValue = effectiveMinDate
                  clamped = true
                }
                if (isAfter(dateValue, maxDate)) {
                  dateValue = maxDate
                  clamped = true
                }
                if (clamped) setToResetKey((k) => k + 1)
                const newDateValue = endOfDay(dateValue)
                handleValueChange({ fromDate: value?.fromDate, toDate: newDateValue })
              }}
              onClose={() => setToIsOpen(false)}
              onOpen={() => setToIsOpen(true)}
              open={toIsOpen}
              slotProps={{
                inputAdornment: {
                  position: 'start',
                  color: toIsOpen ? 'primary' : 'inherit',
                },
                textField: {
                  className: toIsOpen ? 'active' : '',
                  hiddenLabel: true,
                  variant: 'filled',
                  size: 'small',
                  InputProps: {
                    disableUnderline: true,
                    sx: { backgroundColor: 'transparent', '&:hover, &:focus, &:active': { backgroundColor: 'transparent' } },
                  },
                },
              }}
              value={value?.toDate}
              disabled={disabled}
            />
          </Grid>
          <IconButton onClick={handleShowOptionsClick} disabled={disabled}>
            <MoreHoriz sx={{ mx: 1 }} ref={rangeOptionOpener} aria-describedby="ImplExample__dateRangeOptions" />
          </IconButton>
          <Menu
            id="ImplExample__dateRangeOptions"
            open={showOptions}
            onClose={() => setShowOptions(false)}
            anchorEl={rangeOptionOpener.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {dateRangeOptionsHtml}
          </Menu>
        </Grid>
      </LocalizationProvider>
    </FormControl>
  )
}

export default DateRangePicker
